import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Map, { Source, Layer, Marker } from "react-map-gl";
import {
  Button,
  Empty,
  Form,
  Input,
  List,
  Modal,
  notification,
  Radio,
  Spin,
  Switch,
  Table,
  Tooltip,
} from "antd";
import * as htmlToImage from "html-to-image";
import { download } from "@mapbox/shp-write";
import { FaFileDownload } from "react-icons/fa";
import { LuLogIn, LuMap, LuPrinter, LuSave } from "react-icons/lu";
import {
  IoFilter,
  IoGrid,
  IoListOutline,
  IoPrintOutline,
} from "react-icons/io5";
import polylabel from "polylabel";
import { geoArea } from "d3-geo";
import bbox from "@turf/bbox";
import center from "@turf/center";
import axios from "axios";
import { FaAngleDoubleRight } from "react-icons/fa";
import secureLocalStorage from "react-secure-storage";
import Header from "../../Components/Header";
import Footer from "../../Components/Footer";
import hostURL from "../../Common/constants";
import PracticeCard from "../PracticeList/Components/PracticeCard";
import WizardForm from "../ExplorebyGoals/wizardForm";
import {
  openWizard,
  nextWizardStep,
  prevWizardStep,
  setWizardStep,
  closeWizard,
} from "../../Redux/wizardSlice";
import {
  stateFilter,
  stateCode,
  safeLabels,
  landCoverTypeLabels,
  priorityLabels,
  columns,
  safePractices,
  withinFloodplainPractices,
  outsideFloodplainPractices,
  waterBufferPractices,
  crepColorPool,
  crepColorMap,
  crepCodes,
} from "./utils/helperFunctions";
import { setSelectedFips } from "../../Redux/filterSlice";
import { calculatePracticeScore, getMatchLabel } from "./utils/scoring";
import "./style.css";

const FieldReport = () => {
  // ***************** START NEW FILTERING STATE VARAIBLES **************************

  const allPractices = useSelector((state) => state.practice.allPractices);

  // ***************** END NEW FILTERING STATE VARAIBLES **************************
  const [isPageChanging, setIsPageChanging] = useState(false);
  const [printList, setPrintList] = useState(false);
  const [loading, setLoading] = useState(false);
  const [soilData, setSoilData] = useState(null);
  const [safeData, setSafeData] = useState(null);
  const [floodplainData, setFloodplainData] = useState(null);
  const [waterData, setWaterData] = useState(null);
  const [hydroData, setHydroData] = useState(null);
  const [majorSoilTypes, setMajorSoilTypes] = useState([]);
  const [fieldBasics, setFieldBasics] = useState(null);
  const [fieldFeatureData, setFieldFeatureData] = useState(null);
  const [fieldAddress, setFieldAddress] = useState("Not Specified");
  const [fieldSize, setFieldSize] = useState(null);
  const [fieldRentalRate, setFieldRentalRate] = useState(null);
  const [soilTableData, setSoilTableData] = useState(null);
  const [baseLayer, setBaseLayer] = useState(
    "mapbox://styles/mapbox/satellite-streets-v12"
  );
  const [layerSelectOpen, setLayerSelectOpen] = useState(false);
  const [soilLayerOn, setSoilLayerOn] = useState(true);
  const [safeLayerOn, setSafeLayerOn] = useState(false);
  const [floodplainLayerOn, setFloodplainLayerOn] = useState(false);
  const [waterLayerOn, setWaterLayerOn] = useState(false);
  const [interactiveLayerIds, setInteractiveLayerIds] = useState([]);
  const [filter, setFilter] = useState([]);
  const [highlightFeatureId, setHighlightFeatureId] = useState(null);
  const [isSavePopOpen, setIsSavePopOpen] = useState(false);
  const [pageIndex, setPageIndex] = useState(1);
  const [serviceCenterList, setServiceCenterList] = useState([]);
  const [preferredPractice, setPreferredPractice] = useState(null);
  const [userFavoritePractices, setUserFavoritePractices] = useState([]);
  const [userFavoritesLoaded, setUserFavoritesLoaded] = useState(false);
  const [practicesReady, setPracticesReady] = useState(false);

  const [preferredSignups, setPreferredSignups] = useState({
    G: true,
    C: true,
  });
  const [preferredIncentives, setPreferredIncentives] = useState({
    S: 0,
    R: 0,
    P: 0,
    W: 0,
  });
  const [crepOption, setCrepOption] = useState("NO CREP");
  const isWizardOpen = useSelector((state) => state.wizard.open);
  const wizardStep = useSelector((state) => state.wizard.wizardStep);
  const practiceLoading = useSelector(
    (state) => state.practice.practiceLoading
  );
  const navigate = useNavigate();
  const location = useLocation();
  const mapRef = useRef();
  const dispatch = useDispatch();
  // const printPracticeArray = [];
  const [mapLoaded, setMapLoaded] = useState(false);

  let nextColorIndex = 0;

  // const addPrintPracticeArray = (practice) => {
  //   if (!!practice && !printPracticeArray.includes(practice)) {
  //     printPracticeArray.push(practice);
  //   }
  // };

  // const removePrintPracticeArray = (practice) => {
  //   if (!!practice && printPracticeArray.includes(practice)) {
  //     const index = printPracticeArray.indexOf(practice);
  //     if (index > -1) {
  //       printPracticeArray.splice(index, 1);
  //     }
  //   }
  // };

  const handlePrint = () => {
    const origin =
      window.location.hostname === "localhost"
        ? "http://localhost:3000/"
        : hostURL;

    const newWindow = window.open(
      `${origin}FieldReport/Print`,
      "_blank",
      "noopener,noreferrer"
    );

    if (newWindow) {
      newWindow.onload = () => {
        newWindow.contentWindow.postMessage("Hello!!", origin);
      };
    }
  };

  const handleDownload = () => {
    const options = {
      filename: "Field Shapefile",
      outputType: "blob",
      compression: "DEFLATE",
      types: {
        polygon: "field",
      },
    };

    if (fieldFeatureData) {
      download(
        {
          type: "FeatureCollection",
          features: [fieldFeatureData],
        },
        options
      );
    }
  };

  const [form] = Form.useForm();
  const authToken = secureLocalStorage.getItem("token");
  const fieldDetails = secureLocalStorage.getItem("field-details");
  const {
    landCoverSelections,
    prioritySelections,
    rankingScore,
    selectedFips,
    selectedState,
    selectedCounty,
    disableInteractions,
  } = useSelector((state) => state.filter);

  useEffect(() => {
    window.scrollTo({
      top: 0,
    });
  }, []);

  useEffect(() => {
    const polygonFips = fieldBasics?.fips;

    if (!polygonFips) return;

    if (selectedFips && String(polygonFips) !== String(selectedFips)) {
      console.warn("FIPS mismatch — redirecting to draw polygon step");
      navigate("/Map", { replace: true });
    }
  }, [fieldBasics, selectedFips]);

  useEffect(() => {
    dispatch(closeWizard());
  }, []);

  const showPopconfirm = () => {
    setIsSavePopOpen(true);
  };

  const [api, contextHolder] = notification.useNotification();

  const openNotificationWithIcon = (type, title, message) => {
    api[type]({
      message: title,
      description: message,
    });
  };

  const saveSearchPopClose = () => {
    form.resetFields();
    setIsSavePopOpen(false);
  };

  const storeSearchData = async (values, sroute) => {
    form.resetFields();
    setIsSavePopOpen(false);
    let imageDataURL = null;
    setLoading(true);

    await htmlToImage
      .toPng(document.getElementById("fieldReportMap"), { allowTaint: true })
      .then(function (dataurl) {
        imageDataURL = dataurl;
      });

    const searchedData = {
      selectedState: fieldBasics.state,
      selectedCounty: fieldBasics.county,
      landCoverSelections: landCoverSelections,
      prioritySelections: prioritySelections,
      rankingScore: rankingScore,
      mapimage: imageDataURL,
      data: fieldDetails,
    };

    const formData = {
      userId: secureLocalStorage.getItem("userRef"),
      route: window.location.href,
      formData: JSON.stringify(searchedData),
      mySearchName: values.SaveKey,
      stype: "checkEligibility",
    };

    setLoading(true);
    fetch(hostURL + "api/usersearchdata", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: authToken,
      },
      body: JSON.stringify(formData),
    })
      .then((response) => response.json())
      .then((json) => {
        openNotificationWithIcon(
          "success",
          "Success",
          "Saved Your Search Criteria"
        );
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        openNotificationWithIcon("error", "Error", "Try Again Later");
      });
  };

  const getFieldAddress = async () => {
    const fieldGeometry = JSON.parse(fieldDetails.countyRate.rows[0].geometry);
    const centerPoint = center(fieldGeometry);
    const [lng, lat] = centerPoint.geometry.coordinates;
    setLoading(true);
    const response = await axios.get(
      "https://api.mapbox.com/search/geocode/v6/reverse?longitude=" +
        lng +
        "&latitude=" +
        lat +
        "&access_token=pk.eyJ1IjoiY2h1Y2swNTIwIiwiYSI6ImNrMDk2NDFhNTA0bW0zbHVuZTk3dHQ1cGUifQ.dkjP73KdE6JMTiLcUoHvUA"
    );
    if (response.data) {
      setTimeout(() => {
        setLoading(false);
      }, 6000);
      setFieldAddress(response.data.features[0].properties.full_address);
    }
  };

  const getFieldRentalRate = () => {
    const fieldRate = fieldDetails.fieldRate;
    const fieldSpecifics = fieldRate.rows;
    // Remove water from soil types
    const soilTypes = [
      ...new Set(fieldSpecifics.map((field) => field.musym)),
    ].filter((soilType) => soilType !== "W");

    const SoilTypeData = soilTypes.map((soilType) => {
      const fieldsBySoilType = fieldSpecifics?.filter(
        (field) => field.musym === soilType
      );
      const srr = fieldsBySoilType[0].srr;
      const areas = fieldsBySoilType.map((field) => field.area);
      const getTotalArea = (total, area) => {
        return total + area;
      };
      const totalArea = areas.reduce(getTotalArea, 0);
      return { type: soilType, srr: srr, totalArea: totalArea };
    });

    const getTopThree = (arr) => {
      arr.sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));
      return arr.slice(0, 3);
    };

    const totalAreas = SoilTypeData.map((soilType) => soilType.totalArea);
    const topThreeTotalAreas = getTopThree(totalAreas);
    const topThreeSoilTypes = SoilTypeData?.filter((soilType) =>
      topThreeTotalAreas.includes(soilType.totalArea)
    );
    setMajorSoilTypes(topThreeSoilTypes);
  };

  const getPracticeList = (queryState, queryCounty) => {
    setLoading(true);
    fetch(`${hostURL}api/statepractices/${queryState}/${queryCounty}`)
      .then((response) => response.json())
      .then((json) => {
        const respData = json[0];
        dispatch(setSelectedFips(String(respData.fips).padStart(5, "0")));

        // let toShowData =
        //   respData &&
        //   JSON.parse(
        //     respData.assetdata.substring(1, respData.assetdata.length - 1)
        //   );
        // setFilteredPractices(toShowData);
        // setAllPractices(toShowData);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);
      });
  };

  // ****************** NEW FILTERING LOGIC STARTS HERE *************************

  const filteredByPriorityAndLandCover = useMemo(() => {
    return allPractices.filter((practice) => {
      if (
        !Array.isArray(practice.priorities) ||
        !Array.isArray(practice.landcover)
      )
        return false;

      const matchesPriority =
        prioritySelections.length === 0 ||
        prioritySelections.some((selected) =>
          practice.priorities?.some((p) => p.name === selected)
        );

      const matchesLandCover =
        landCoverSelections.length === 0 ||
        landCoverSelections.some((selected) =>
          practice.landcover?.includes(selected)
        );

      return matchesPriority && matchesLandCover;
    });
  }, [allPractices, prioritySelections, landCoverSelections]);

  console.log(
    "✅ Step 1: matched land cover + priority",
    filteredByPriorityAndLandCover.map((p) => p.title)
  );

  const waterBufferFilteredPractices = useMemo(() => {
    const fieldHasWaterBuffer = waterData?.features?.length > 0;

    return filteredByPriorityAndLandCover.filter((practice) => {
      const practiceCode = practice.practiceNumber;
      const requiresWaterBuffer =
        waterBufferPractices.hasOwnProperty(practiceCode);

      if (requiresWaterBuffer && !fieldHasWaterBuffer) return false;
      return true;
    });
  }, [filteredByPriorityAndLandCover, waterData, waterBufferPractices]);

  console.log(
    "✅ Step 2: after water buffer",
    waterBufferFilteredPractices.map((p) => p.title)
  );

  const floodplainFilteredPractices = useMemo(() => {
    const fieldHasFloodplain = floodplainData?.features?.length > 0;

    return waterBufferFilteredPractices.filter((practice) => {
      const practiceCode = practice.practiceNumber;
      const mustBeInFloodplain =
        withinFloodplainPractices.includes(practiceCode);
      const mustBeOutsideFloodplain =
        outsideFloodplainPractices.includes(practiceCode);

      if (mustBeInFloodplain && !fieldHasFloodplain) return false;
      if (mustBeOutsideFloodplain && fieldHasFloodplain) return false;

      return true;
    });
  }, [
    waterBufferFilteredPractices,
    floodplainData,
    withinFloodplainPractices,
    outsideFloodplainPractices,
  ]);

  console.log(
    "✅ Step 3: after floodplain",
    floodplainFilteredPractices.map((p) => p.title)
  );

  const safeFilteredPractices = floodplainFilteredPractices.filter(
    (practice) => {
      const fieldHasSafeZone = safeData?.features?.length > 0;
      const safePracticeTitles = Object.values(safePractices); // or adjust if already an array
      const isSafePractice = safePracticeTitles.includes(practice.title);

      if (isSafePractice && !fieldHasSafeZone) {
        return false; // exclude SAFE practices if field not in SAFE zone
      }

      return true; // include all else
    }
  );

  console.log(
    "✅ Step 4: after SAFE",
    safeFilteredPractices.map((p) => p.title)
  );

  const hydroFilteredPractices = safeFilteredPractices.filter((practice) => {
    const fieldTotalArea = hydroData?.features
      ?.map((feature) => feature.properties.area)
      .reduce((a, b) => a + b, 0);
    const fieldHydricArea = hydroData?.features
      ?.map((feature) => {
        if (feature.properties.gridcode > 50) {
          return feature.properties.area;
        } else {
          return 0;
        }
      })
      .reduce((a, b) => a + b, 0);
    const fieldHasHydricSoil =
      fieldHydricArea / fieldTotalArea > 0.5 ? true : false;

    if (practice.practiceNumber === "CP23" && !fieldHasHydricSoil) {
      return false;
    } else {
      return true;
    }
  });

  console.log(
    "✅ Step 5: after hydric soil",
    hydroFilteredPractices.map((p) => p.title)
  );

  const scoredAndSortedPractices = useMemo(() => {
    return hydroFilteredPractices
      .map((practice) => {
        const score = calculatePracticeScore(practice, rankingScore);
        const { scoreLabel, scoreClassName } = getMatchLabel(
          score,
          prioritySelections.length
        );

        return {
          ...practice,
          score,
          scoreLabel,
          scoreClassName,
        };
      })
      .sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }

        const getCPNum = (p) => {
          const match = p.practiceNumber?.match(/\d+/);
          return match ? parseInt(match[0], 10) : 0;
        };

        return getCPNum(a) - getCPNum(b);
      });
  }, [hydroFilteredPractices, rankingScore, prioritySelections]);

  // ****************** NEW FILTERING LOGIC ENDS HERE *************************
  useEffect(() => {
    setPageIndex(1);
  }, [prioritySelections, landCoverSelections]);

  const getUserFavorites = () => {
    fetch(
      `${hostURL}api/userfavourite/${secureLocalStorage.getItem(
        "userRef"
      )}/${selectedState}/${selectedCounty}`,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: authToken,
        },
      }
    )
      .then((response) => response.json())
      .then((json) => {
        const rawFavorites = Array.isArray(json)
          ? json.map((item) => item.favTitle)
          : json.data && Array.isArray(json.data)
          ? json.data.map((item) => item.favTitle)
          : [];

        const respData = Array.from(new Set(rawFavorites));
        setUserFavoritePractices(respData);
        setUserFavoritesLoaded(true); // ✅ mark as ready
      })
      .catch((error) => {
        console.error("❌ Error fetching favorites:", error);
        setUserFavoritesLoaded(true); // still mark as ready to unblock UI
      });
  };

  useEffect(() => {
    const userId = secureLocalStorage.getItem("userRef");
    if (!userId) {
      setUserFavoritesLoaded(true); // not logged in — skip
      return;
    }

    if (selectedState && selectedCounty && !userFavoritesLoaded) {
      getUserFavorites();
    }
  }, [selectedState, selectedCounty]);

  const favOnchange = (val, title) => {
    const pTitle = title.split("#")[0];
    const pLink = title.split("#")[1];
    if (val === 1) {
      const formData = {
        userId: secureLocalStorage.getItem("userRef"),
        favTitle: pTitle,
        favPdfLink: pLink,
        state: selectedState,
        county: selectedCounty,
      };

      setLoading(true);
      fetch(hostURL + "api/userfavourite", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: authToken,
        },
        body: JSON.stringify(formData),
      })
        .then((response) => response.json())
        .then((json) => {
          getUserFavorites();
          openNotificationWithIcon(
            "success",
            "Success",
            "Added to your Favorites"
          );
          setLoading(false);
        })
        .catch((error) => {
          openNotificationWithIcon("error", "Error", "Try Again Later");
        });
    } else if (val === 0) {
      const formData = {
        userId: secureLocalStorage.getItem("userRef"),
        favTitle: pTitle,
        state: selectedState,
        county: selectedCounty,
      };

      setLoading(true);
      fetch(
        `${hostURL}api/userfavourite/${formData.userId}/${formData.favTitle}/${formData.state}/${formData.county}`,
        {
          method: "DELETE",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: authToken,
          },
          body: JSON.stringify(formData),
        }
      )
        .then((response) => response.json())
        .then((json) => {
          getUserFavorites();
          openNotificationWithIcon(
            "success",
            "Success",
            "Removed from your Favorites"
          );
          setLoading(false);
        })
        .catch((error) => {
          setLoading(false);
          openNotificationWithIcon("error", "Error", "Try Again Later");
        });
    }
  };

  const getServiceCenter = async (queryState, queryCounty) => {
    const response = await axios.post(hostURL + "api/field/service-center", {
      state: queryState,
      county: queryCounty,
    });
    if (response) {
      const fsaServiceCenters = response.data.serviceCenter.rows.filter(
        (item) => item.service_center.includes("Farm Service Agency")
      );
      setServiceCenterList(fsaServiceCenters);
    }
  };

  const handleLayerSelect = () => {
    setLayerSelectOpen(!layerSelectOpen);
  };

  const getMarkerLocation = (geometry) => {
    let output = [];
    if (geometry.type === "Polygon") {
      output = polylabel(geometry.coordinates, 0.000001);
    } else {
      let maxArea = 0,
        maxPolygon = [];
      for (let i = 0, l = geometry.coordinates.length; i < l; i++) {
        const p = geometry.coordinates[i];
        const area = geoArea({ type: "Polygon", coordinates: p });
        if (area > maxArea) {
          maxPolygon = p;
          maxArea = area;
        }
      }
      output = polylabel(maxPolygon, 0.000001);
    }
    return output;
  };

  const onMouseMove = (e) => {
    if (e.features.length) {
      const feature = e.features[0];
      setFilter(["in", "gid", feature.id]);
      setHighlightFeatureId(feature.id);
    }
  };

  // useEffect(() => {
  //   if (fieldBasics) {
  //     setLoading(true);
  //     fetch(hostURL + "api/counties?state=" + stateCode[fieldBasics.state])
  //       .then((response) => response.json())
  //       .then((json) => {
  //         setCountyListData(json);
  //         setLoading(false);
  //       })
  //       .catch((error) => console.error(error));
  //   }
  // }, [fieldBasics]);

  // useEffect(() => {
  //   if (countyListData && fieldBasics) {
  //     let countyDataArray = countyListData?.filter(
  //       (item) => item.cname === fieldBasics.county
  //     );
  //     let assetData =
  //       countyDataArray.length > 0 ? countyDataArray[0].assetdata : null;
  //     let topPracticeList = assetData
  //       ? assetData.replace('"', "")?.split(",")
  //       : null;
  //     setselectedTopPracticeList(topPracticeList);
  //   }
  // }, [countyListData, fieldBasics]);

  useEffect(() => {
    if (fieldDetails) {
      const basics = fieldDetails.countyRate.rows[0];
      setFieldBasics(basics);
      getFieldRentalRate();
      setFieldSize(basics.area * 0.000247105);
      setFieldFeatureData({
        type: "Feature",
        geometry: JSON.parse(basics.geometry),
      });
      getPracticeList(stateCode[basics.state], basics.county);
      getServiceCenter(basics.state, basics.county);
      getFieldAddress();

      // Setup soil, floodplain, safe, and water buffer layers
      const soilTableData = fieldDetails.fieldRate.rows.map((soil, index) => {
        return {
          key: soil.gid,
          soil_type: soil.musym,
          rental_rate: "$" + soil.srr + "/acre",
          acreage: Math.round(soil.area * 0.000247105 * 100) / 100,
        };
      });
      setSoilTableData(soilTableData.sort((a, b) => b.acreage - a.acreage));

      const soilFeatures = fieldDetails.fieldRate.rows.map((soil) => {
        const soilGeometry = JSON.parse(soil.geometry);
        return {
          type: "Feature",
          geometry: soilGeometry,
          id: soil.gid,
          properties: { gid: soil.gid, musym: soil.musym },
        };
      });
      const soilFeatureCollection = {
        type: "FeatureCollection",
        features: soilFeatures,
      };
      setSoilData(soilFeatureCollection);
      setInteractiveLayerIds(["soil-layer"]);

      if (fieldDetails.safeArea.rows.length > 0) {
        const safeFeatures = fieldDetails.safeArea.rows.map((safe) => {
          const safeGeometry = JSON.parse(safe.geometry);
          return { type: "Feature", geometry: safeGeometry };
        });
        const safeFeatureCollection = {
          type: "FeatureCollection",
          features: safeFeatures,
        };
        setSafeData(safeFeatureCollection);
      }

      if (fieldDetails.floodPlain.rows.length > 0) {
        const floodplainFeatures = fieldDetails.floodPlain.rows.map(
          (floodplain) => {
            const floodplainGeometry = JSON.parse(floodplain.geometry);
            return { type: "Feature", geometry: floodplainGeometry };
          }
        );
        const floodplainFeatureCollection = {
          type: "FeatureCollection",
          features: floodplainFeatures,
        };
        setFloodplainData(floodplainFeatureCollection);
      }

      if (fieldDetails.waterBuffer.rows.length > 0) {
        const waterBufferFeatures = fieldDetails.waterBuffer.rows.map(
          (waterBuffer) => {
            const waterBufferGeometry = JSON.parse(waterBuffer.geometry);
            return { type: "Feature", geometry: waterBufferGeometry };
          }
        );
        const waterBufferFeatureCollection = {
          type: "FeatureCollection",
          features: waterBufferFeatures,
        };
        setWaterData(waterBufferFeatureCollection);
      }

      if (fieldDetails.hydricSoil.rows.length > 0) {
        const hydricSoilFeatures = fieldDetails.hydricSoil.rows.map(
          (hydricSoil) => {
            const hydricSoilGeometry = JSON.parse(hydricSoil.geometry);
            return {
              type: "Feature",
              geometry: hydricSoilGeometry,
              id: hydricSoil.gid,
              properties: {
                gridcode: hydricSoil.gridcode,
                area: hydricSoil.area,
              },
            };
          }
        );
        const hydricSoilFeatureCollection = {
          type: "FeatureCollection",
          features: hydricSoilFeatures,
        };
        setHydroData(hydricSoilFeatureCollection);
      }

      secureLocalStorage.setItem("field-details", fieldDetails);
    }
  }, [fieldDetails]);

  useEffect(() => {
    const rateBySoilTypes = majorSoilTypes.map((soilType) => {
      const areaInAcre = soilType.totalArea * 0.0002471;
      const rateBySoilType = soilType.srr * areaInAcre;
      return rateBySoilType;
    });
    const getTotalRate = (total, rate) => {
      return total + rate;
    };
    const totalRentalRate = Math.round(rateBySoilTypes.reduce(getTotalRate, 0));
    setFieldRentalRate(totalRentalRate);
  }, [majorSoilTypes]);

  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !fieldFeatureData) return;

    const [minLng, minLat, maxLng, maxLat] = bbox(fieldFeatureData);
    mapRef.current.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      { padding: 40, duration: 1000 }
    );
  }, [fieldFeatureData, mapLoaded]);

  // const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // useEffect(() => {
  //   const handleResize = () => setWindowWidth(window.innerWidth);
  //   window.addEventListener("resize", handleResize);
  //   return () => window.removeEventListener("resize", handleResize);
  // }, []);

  useEffect(() => {
    if (
      userFavoritesLoaded &&
      !practiceLoading &&
      scoredAndSortedPractices.length > 0
    ) {
      // Delay a tick to let UI catch up
      setTimeout(() => setPracticesReady(true), 50);
    }
  }, [userFavoritesLoaded, practiceLoading, scoredAndSortedPractices]);

  if (!fieldDetails) {
    navigate("/");
    return;
  }
  const practiceNoteData = {
    CP8A: "Note: CP8A grass waterways must not exceed a maximum width of 100ft. Click Details for more information on CP8A.",
    CP9: "Note: CP9 practice enrollment must not exceed 10 acres. The buffer area must be a minimum width of 20ft and maximum width of 120ft. Click Details for more information on CP9.",
    CP12: "Note: CP12 must be enrolled in conjunction with another practice. Wildlife food plots must not exceed 5 acres. Click Details for more information on CP12.",
    CP15A:
      "Note: CP15A strip widths may vary between 15 and 60ft. Click Details for more information on CP15A.",
    CP15B:
      "Note: CP15B strip widths are generally 10ft wide. Click Details for more information on CP15B.",
    CP21: "Note: CP21 filter strips must be immediately adjacent to and parallel to an identified water body. Filter strips must be a minimum of 20ft and maximum of 120ft. Click Details for more information on CP21.",
    CP22: "Note: CP22 buffers must be immediately adjacent to and parallel to an identified water body. A riparian buffer must be a minimum of 35ft and maximum of 180 ft. Click Details for more information on CP22.",
    CP23: "Note: The CP23 buffer to wetland ratio must not exceed 3:1. Click Details for more information on CP23.",
    CP23A:
      "Note: The CP23A buffer to wetland ratio must not exceed 4:1. Click Details for more information on CP23A.",
    CP27: "Note: Land eligible for CP27 must be under 40 acres. Click Details for more information on CP40.",
    CP28: "Note: CP28 buffers must be a minimum of 30ft around a wetland and maximum of 4x the wetland size. Click Details for more information on CP28.",
    CP29: "Note: CP29 buffers must be immediately adjacent and parallel to an identified water body. A marginal pastureland wildlife buffer must be a minimum of 20ft and maximum of 120ft. Click Details for more information on CP29.",
    CP30: "Note: CP30 buffers must be immediately adjacent and parallel to an identified water body. A marginal pastureland wetland buffer must be a minimum of 20ft and maximum of 120ft. Click Details for more information on CP30.",
    CP33: "Note: CP33 buffers must be a minimum average of 30ft and maximum average of 120ft. Fields must be greater than 5 acres to be eligible for CP33. Click Details for more information on CP33.",
    CP37: "Note: CP37 practices must be located outside of the 100-year floodplain. The buffer to wetland ratio must be a minimum of 4:1 and maximum of 10:1. Click Details for more information on CP37.",
    CP39: "Note: CP39 practice site must not exceed 40 acres and buffer area must not exceed 4x the size of the wetland. Click Details for more information on CP39.",
    CP41: "Note: CP41 practice site must not exceed 40 acres, with a maximum of 20 acres of wetland. The buffer area must not exceed 4x the size of the wetland. Click Details for more information on CP41.",
    CP42: "Note: CP42 maximum enrollment is 10 acres and strips must be a minimum of 20ft wide. Click Details for more information on CP42.",
    CP43: "Note: CP43 strip must be a minimum of 30ft and a maximum of 120ft. Click Details for more information on CP43.",
  };

  return (
    <>
      <Header />
      {contextHolder}
      <Spin spinning={loading}>
        <div className="report">
          <div className="report-head-container">
            <div style={{ display: "flex", flexDirection: "column" }}>
              <h2
                className={printList ? "print report-header" : "report-header"}
              >
                CRP Field Eligibility Report:
                <span
                  style={{ fontWeight: "normal" }}
                >{` ${fieldBasics?.county}, ${fieldBasics?.state}`}</span>
              </h2>
              <div className="basic-info-container">
                <div className="report-basic-information">
                  <p></p>
                  <p>
                    <b>FIPS Code: </b>
                    {fieldBasics?.fips} | <b>Field Size: ≈ </b>
                    {Math.floor(fieldSize)} acre |{" "}
                    {/* <b>County Average Rental Rate*: </b>$
                    {Math.round(fieldBasics?.srr2024 * 100) / 100}/acre */}
                  </p>
                  <p>
                    <b>SAFE Zone(s): </b>
                    {fieldDetails.safeArea.rows.length === 0
                      ? "No"
                      : fieldDetails.safeArea.rows
                          .map((item) => safeLabels[item.code])
                          .join(", ")}{" "}
                    | <b>Floodplain: </b>
                    {fieldDetails.floodPlain.rows.length ? "Yes" : "No"} |{" "}
                    <b>Selected Priorities: </b>
                    {prioritySelections.length
                      ? prioritySelections.map((priority, index) => {
                          if (index === 0) {
                            return priorityLabels[priority];
                          } else {
                            return ", " + priorityLabels[priority];
                          }
                        })
                      : "No priority selected"}
                  </p>
                  <Button
                    className="report-button filter-button"
                    onClick={() => {
                      if (wizardStep < 1) dispatch(setWizardStep(1));
                      dispatch(openWizard());
                    }}
                  >
                    <IoFilter style={{ marginRight: 5 }} />
                    Update Filters
                  </Button>
                </div>
              </div>
            </div>
            <div className="report-head-btn-container">
              {authToken === null ? (
                <Button
                  className="report-button"
                  onClick={() =>
                    navigate("/UserLogin", { state: { from: location } })
                  }
                >
                  <LuLogIn className="report-button-icon" />
                  Log In To Save
                </Button>
              ) : (
                <Button className="report-button" onClick={showPopconfirm}>
                  <LuSave className="report-button-icon" />
                  Save Field Report
                </Button>
              )}
              <Button className="report-button" onClick={handlePrint}>
                <LuPrinter className="report-button-icon" />
                Print
              </Button>
              <Button className="report-button" onClick={handleDownload}>
                <FaFileDownload className="report-button-icon" />
                Download Shapefile
              </Button>
            </div>
          </div>
          <hr className="report-hr" />
          <div className="map-practice-container">
            <div className="report-practice-container">
              <h3
                className={
                  printList
                    ? "print report-section-header"
                    : "report-section-header"
                }
              >
                Eligible CRP Practices:
              </h3>

              <p style={{ width: "90%", marginTop: "-5px" }}>
                Please click on a practice below to view practice specific field
                rental rates and use the <em> Details </em> link for more
                information on each practice.
              </p>
              <Spin spinning={!practicesReady}>
                {practicesReady && (
                  <List
                    className="report-practice-list"
                    grid={{ gutter: 16, column: 1 }}
                    locale={{
                      emptyText: (
                        <Empty description="No eligible practices found for your selections." />
                      ),
                    }}
                    pagination={{
                      align: "center",
                      position: "bottom",
                      pageSize: 6,
                      current: pageIndex,
                      total: scoredAndSortedPractices?.length || 0,
                      onChange: (page) => setPageIndex(page),
                    }}
                    dataSource={scoredAndSortedPractices?.slice(
                      (pageIndex - 1) * 6,
                      pageIndex * 6
                    )}
                    renderItem={(practice, index) => {
                      const practiceTitle = practice.title
                        ?.split("-")[0]
                        .trim();

                      const handlePracticeClick = () => {
                        if (preferredPractice !== practiceTitle) {
                          setPreferredPractice(practiceTitle);
                          setPreferredSignups({
                            G: !!practice.general,
                            C: !!practice.continuous,
                          });
                          const updatedIncentives = { ...preferredIncentives };

                          practice.programs.forEach((p) => {
                            const { incentives } = p;
                            if (!incentives) return;
                            const sip = Number(incentives.sip);
                            if (!isNaN(sip))
                              updatedIncentives.S = Math.max(
                                updatedIncentives.S,
                                sip
                              );
                            const rri = Number(incentives.rentalRateIncentive);
                            if (!isNaN(rri))
                              updatedIncentives.R = Math.max(
                                updatedIncentives.R,
                                rri
                              );
                            const pip = Number(incentives.pip);
                            if (!isNaN(pip))
                              updatedIncentives.P = Math.max(
                                updatedIncentives.P,
                                pip
                              );
                            const wq = Number(
                              incentives.waterqualitythruclear30
                            );
                            if (!isNaN(wq))
                              updatedIncentives.W = Math.max(
                                updatedIncentives.W,
                                wq
                              );
                          });

                          setPreferredIncentives(updatedIncentives);
                        } else {
                          setPreferredPractice(null);
                        }
                        setCrepOption("NO CREP");
                      };

                      const getEstimatedPayment = (paymentMultiplier) => {
                        const sipMultiplier = preferredIncentives.S
                          ? 1 + preferredIncentives.S / 100
                          : 1;
                        const rentalRateMultiplier = preferredIncentives.R
                          ? 1 + preferredIncentives.R / 100
                          : 1;
                        const pipMultiplier = preferredIncentives.P
                          ? 1 + preferredIncentives.P / 100
                          : 1;

                        const totalFieldPayment = Math.round(
                          fieldRentalRate *
                            sipMultiplier *
                            rentalRateMultiplier *
                            pipMultiplier *
                            paymentMultiplier
                        );

                        const maximumFieldPayment =
                          paymentMultiplier === 0.9
                            ? Math.round(300 * fieldSize)
                            : paymentMultiplier === 0.85
                            ? Math.round(240 * fieldSize)
                            : totalFieldPayment;

                        const estimatedFieldPayment =
                          totalFieldPayment <= maximumFieldPayment
                            ? totalFieldPayment
                            : maximumFieldPayment;

                        return estimatedFieldPayment;
                      };

                      practice.crepInfo?.forEach((crep) => {
                        const title = crep.crepname;
                        if (!crepColorMap.has(title)) {
                          crepColorMap.set(
                            title,
                            crepColorPool[nextColorIndex % crepColorPool.length]
                          );
                          nextColorIndex++;
                        }
                      });

                      return (
                        <List.Item key={index}>
                          <div className="report-practice-detail-container">
                            <button
                              onClick={handlePracticeClick}
                              className="practice-card-container"
                            >
                              <PracticeCard
                                mode="check_eligibility"
                                disableInteractions={true}
                                practice={practice}
                                listView="Compact"
                                selectable={true}
                                favOnchange={favOnchange}
                                selected={preferredPractice === practiceTitle}
                                preferredPractice={preferredPractice}
                                setPreferredPractice={setPreferredPractice}
                                setPreferredSignups={setPreferredSignups}
                                setPreferredIncentives={setPreferredIncentives}
                                userFavoritePractices={userFavoritePractices}
                                crepColorMap={crepColorMap}
                                isMostCommon={practice.isMostCommon}
                              />
                            </button>
                            <div
                              className="report-practice-detail"
                              style={{
                                ...(preferredPractice !== practiceTitle && {
                                  borderBottom: "none",
                                  paddingBottom: "0",
                                }),
                              }}
                            >
                              {preferredPractice === practiceTitle && (
                                <>
                                  {practice.crepInfo && (
                                    <Radio.Group
                                      className="report-crep-options"
                                      defaultValue="NO CREP"
                                      value={crepOption}
                                      onChange={(e) =>
                                        setCrepOption(e.target.value)
                                      }
                                    >
                                      <Radio.Button
                                        className="report-crep-button"
                                        value="NO CREP"
                                        style={{
                                          color: "white",
                                          backgroundColor:
                                            crepOption === "NO CREP"
                                              ? "#808080"
                                              : "#80808066",
                                          border: "1px white solid",
                                          borderRadius: "10px 10px 0px 0px",
                                        }}
                                      >
                                        {preferredSignups.G && (
                                          <span>General </span>
                                        )}
                                        {preferredSignups.G &&
                                          preferredSignups.C && <span>&</span>}
                                        {preferredSignups.C && (
                                          <span> Continuous</span>
                                        )}
                                        <span> Signup</span>
                                      </Radio.Button>

                                      {practice.crepInfo
                                        .filter((crep) =>
                                          fieldDetails.crepArea.rows
                                            .map((item) => item.code)
                                            .includes(crepCodes[crep.crepname])
                                        )
                                        .map((option) => {
                                          const baseColor =
                                            crepColorMap?.get(
                                              option.crepname
                                            ) || "#888888";
                                          const isSelected =
                                            crepOption === option.crepname;

                                          return (
                                            <Radio.Button
                                              key={option.crepname}
                                              className="report-crep-button"
                                              value={option.crepname}
                                              style={{
                                                color: "white",
                                                backgroundColor: isSelected
                                                  ? baseColor
                                                  : `${baseColor}66`,
                                                border: "1px white solid",
                                                borderRadius:
                                                  "10px 10px 0px 0px",
                                              }}
                                            >
                                              {option.crepname}
                                            </Radio.Button>
                                          );
                                        })}
                                    </Radio.Group>
                                  )}

                                  {crepOption === "NO CREP" && (
                                    <div
                                      className="report-practice-list-short"
                                      style={{
                                        border: "2px solid gray",
                                      }}
                                    >
                                      <h3>
                                        Your Estimated Field Rental Rate is{" "}
                                        {preferredSignups.G && (
                                          <span>
                                            $
                                            {getEstimatedPayment(
                                              0.85
                                            ).toLocaleString()}{" "}
                                            (General Signup)
                                            <sup>[1]</sup>
                                          </span>
                                        )}
                                        {preferredSignups.G &&
                                        preferredSignups.C
                                          ? " or "
                                          : ""}
                                        {preferredSignups.C && (
                                          <span>
                                            $
                                            {getEstimatedPayment(
                                              0.9
                                            ).toLocaleString()}{" "}
                                            (Continuous Signup)
                                            <sup>[1]</sup>
                                          </span>
                                        )}
                                      </h3>
                                      <div>
                                        <p>
                                          The top soil types in your field are{" "}
                                          {majorSoilTypes &&
                                            majorSoilTypes.map(
                                              (soilType, index) => (
                                                <span key={soilType.type}>
                                                  {`${soilType.type} (${
                                                    Math.round(
                                                      soilType.totalArea *
                                                        0.0002471 *
                                                        100
                                                    ) / 100
                                                  } acres @ $${
                                                    soilType.srr
                                                  }/acre)`}
                                                  {index <
                                                    majorSoilTypes.length - 1 &&
                                                    ", "}
                                                </span>
                                              )
                                            )}
                                          . To understand these soil type codes,
                                          please find the details{" "}
                                          <a
                                            href="https://www.nrcs.usda.gov/publications/Map%20Unit%20Description%20%28Brief%2C%20Generated%29%20Query%20by%20Soil%20Survey%20Area%20and%20Map%20Unit%20%28All%20Components%29.html"
                                            target="_blank"
                                            style={{ color: "maroon" }}
                                            rel="noreferrer"
                                          >
                                            here
                                          </a>
                                          .
                                        </p>
                                        <p>
                                          Your total weighted average rental
                                          rate is estimated to be:
                                          <b>
                                            {" "}
                                            ${fieldRentalRate.toLocaleString()}
                                          </b>
                                          <sup>[1]</sup>. Your total weighted
                                          average rental rate per acre is
                                          estimated to be:
                                          <b>
                                            {" "}
                                            $
                                            {Math.round(
                                              fieldRentalRate / fieldSize
                                            ).toLocaleString()}
                                            /acre
                                          </b>
                                          <sup>[1]</sup>. As determined by UDSA,
                                          landowners will receive 85% for
                                          general signup and 90% for continuous
                                          signup of the estimated rental rate.
                                        </p>
                                        <p>
                                          By signing up for{" "}
                                          <b>{preferredPractice}</b>,
                                          {(preferredIncentives.S ||
                                            preferredIncentives.R ||
                                            preferredIncentives.P) && (
                                            <span> you are eligible for </span>
                                          )}
                                          {preferredIncentives.S && (
                                            <span>
                                              a 32.5% signup incentive,{" "}
                                            </span>
                                          )}
                                          {preferredIncentives.R && (
                                            <span>
                                              a 20% rental rate incentive,{" "}
                                            </span>
                                          )}
                                          {preferredIncentives.P && (
                                            <span>
                                              a 50% practice incentive,{" "}
                                            </span>
                                          )}
                                          and <b>{preferredPractice}</b>{" "}
                                          supports{" "}
                                          {!preferredSignups.C ? (
                                            <>
                                              general signup, which has a
                                              maximum payment cap set at
                                              $240/acre. This results in an
                                              estimated potential first-year
                                              payment of{" "}
                                              <b>
                                                $
                                                {getEstimatedPayment(
                                                  0.85
                                                ).toLocaleString()}
                                              </b>{" "}
                                              for your field.
                                            </>
                                          ) : !preferredSignups.G ? (
                                            <>
                                              continuous signup, which has a
                                              maximum payment cap set at
                                              $300/acre. This results in an
                                              estimated potential first-year
                                              payment of{" "}
                                              <b>
                                                $
                                                {getEstimatedPayment(
                                                  0.9
                                                ).toLocaleString()}
                                              </b>{" "}
                                              for your field.
                                            </>
                                          ) : (
                                            "both general and continuous signup."
                                          )}
                                          {preferredSignups.C &&
                                            preferredSignups.G && (
                                              <>
                                                <span>
                                                  {" "}
                                                  For general signup, the cap is
                                                  set at $240/acre, resulting in
                                                  an estimated potential
                                                  first-year payment of{" "}
                                                  <b>
                                                    $
                                                    {getEstimatedPayment(
                                                      0.85
                                                    ).toLocaleString()}
                                                  </b>{" "}
                                                  for your field.
                                                </span>
                                                <span>
                                                  {" "}
                                                  For continuous signup, the cap
                                                  is set at $300/acre, resulting
                                                  in an estimated potential
                                                  first-year payment of{" "}
                                                  <b>
                                                    $
                                                    {getEstimatedPayment(
                                                      0.9
                                                    ).toLocaleString()}
                                                  </b>
                                                  <sup>[1]</sup> for your field.
                                                </span>
                                              </>
                                            )}
                                        </p>
                                        <p>
                                          {practiceNoteData[preferredPractice]}
                                        </p>
                                      </div>
                                      {disableInteractions ? (
                                        <p
                                          className={
                                            "detail-right field-report"
                                          }
                                        >
                                          <p className="detail-button">
                                            <a
                                              href={`/Practice/${selectedFips}/${btoa(
                                                practice.pdfLink
                                              )}/${btoa(practice.title)}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              style={{
                                                color: "maroon",
                                                fontWeight: "bold",
                                              }}
                                            >
                                              Details &raquo;
                                            </a>
                                          </p>
                                        </p>
                                      ) : (
                                        <p
                                          className={
                                            "detail-right field-report"
                                          }
                                        >
                                          <p className="detail-button">
                                            <a
                                              href={`/Practice/${selectedFips}/${btoa(
                                                practice.pdfLink
                                              )}/${btoa(practice.title)}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              style={{
                                                color: "maroon",
                                                fontWeight: "bold",
                                              }}
                                            >
                                              Details <FaAngleDoubleRight />
                                            </a>
                                          </p>
                                        </p>
                                      )}
                                    </div>
                                  )}

                                  {practice.crepInfo &&
                                    practice.crepInfo
                                      .filter((crep) =>
                                        fieldDetails.crepArea.rows
                                          .map((item) => item.code)
                                          .includes(crepCodes[crep.crepname])
                                      )
                                      .map(
                                        (option) =>
                                          crepOption === option.crepname && (
                                            <div
                                              className="report-practice-list-short"
                                              style={{
                                                border: `2px solid ${crepColorMap?.get(
                                                  option.crepname
                                                )}`,
                                              }}
                                            >
                                              <h3>
                                                <span>
                                                  Your Estimated Field Rental
                                                  Rate is $
                                                  {Math.round(
                                                    getEstimatedPayment(1) *
                                                      (parseInt(
                                                        option.newenrollmentsrr
                                                      ) /
                                                        100) +
                                                      parseInt(
                                                        option.stateincentive
                                                      ) *
                                                        fieldSize
                                                  ).toLocaleString()}
                                                  ({option.crepname} Initiative)
                                                  <sup>[1]</sup>
                                                </span>
                                              </h3>
                                              <div>
                                                <p>
                                                  The top soil types in your
                                                  field are{" "}
                                                  {majorSoilTypes &&
                                                    majorSoilTypes.map(
                                                      (soilType, index) => (
                                                        <span
                                                          key={soilType.type}
                                                        >
                                                          {`${soilType.type} (${
                                                            Math.round(
                                                              soilType.totalArea *
                                                                0.0002471 *
                                                                100
                                                            ) / 100
                                                          } acres @ $${
                                                            soilType.srr
                                                          }/acre)`}
                                                          {index <
                                                            majorSoilTypes.length -
                                                              1 && ", "}
                                                        </span>
                                                      )
                                                    )}
                                                  . To understand these soil
                                                  type codes, please find the
                                                  details{" "}
                                                  <a
                                                    href="https://www.nrcs.usda.gov/publications/Map%20Unit%20Description%20%28Brief%2C%20Generated%29%20Query%20by%20Soil%20Survey%20Area%20and%20Map%20Unit%20%28All%20Components%29.html"
                                                    target="_blank"
                                                    style={{
                                                      color: "maroon",
                                                    }}
                                                    rel="noreferrer"
                                                  >
                                                    here
                                                  </a>
                                                  .
                                                </p>
                                                <p>
                                                  Your total weighted average
                                                  rental rate is estimated to
                                                  be:
                                                  <b>
                                                    {" "}
                                                    $
                                                    {fieldRentalRate.toLocaleString()}
                                                  </b>
                                                  <sup>[1]</sup>. Your total
                                                  weighted average rental rate
                                                  per acre is estimated to be:
                                                  <b>
                                                    {" "}
                                                    $
                                                    {Math.round(
                                                      fieldRentalRate /
                                                        fieldSize
                                                    ).toLocaleString()}
                                                    /acre
                                                  </b>
                                                  <sup>[1]</sup>. Enrollment
                                                  through
                                                  <b>
                                                    {" "}
                                                    {option.crepname}{" "}
                                                  </b>{" "}
                                                  includes additional
                                                  incentives, such as an annual{" "}
                                                  <b>
                                                    {" "}
                                                    {
                                                      option.newenrollmentsrr
                                                    }%{" "}
                                                  </b>
                                                  soil rental rate for new
                                                  enrollments and annual
                                                  <b>
                                                    {" "}
                                                    $
                                                    {Number(
                                                      option.stateincentive
                                                    ).toLocaleString()}
                                                    /acre{" "}
                                                  </b>
                                                  state incentive. This results
                                                  in an estimated potential
                                                  first-year payment of
                                                  <b>
                                                    {" "}
                                                    $
                                                    {Math.round(
                                                      getEstimatedPayment(1) *
                                                        (parseInt(
                                                          option.newenrollmentsrr
                                                        ) /
                                                          100) +
                                                        parseInt(
                                                          option.stateincentive
                                                        ) *
                                                          fieldSize
                                                    ).toLocaleString()}
                                                  </b>
                                                  <sup>[1]</sup> for your field.
                                                </p>
                                                <p>
                                                  Please contact your local FSA
                                                  office for more details and
                                                  official offers.
                                                </p>
                                              </div>

                                              <p className="detail-right field-report">
                                                <p className="detail-button">
                                                  <a
                                                    href={`/Practice/${selectedFips}/${btoa(
                                                      practice.pdfLink
                                                    )}/${btoa(
                                                      practice.title
                                                    )}/${btoa(
                                                      option.crepname
                                                    )}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                      color: "maroon",
                                                      fontWeight: "bold",
                                                    }}
                                                  >
                                                    Details{" "}
                                                    <FaAngleDoubleRight />{" "}
                                                    <br />
                                                  </a>
                                                </p>
                                              </p>
                                            </div>
                                          )
                                      )}
                                </>
                              )}
                            </div>
                          </div>
                        </List.Item>
                      );
                    }}
                  />
                )}
              </Spin>
            </div>
            <div className="map-rental-container">
              <div className="report-map-container">
                <h3
                  className={
                    printList
                      ? "print report-section-header"
                      : "report-section-header"
                  }
                >
                  Field Boundary:
                </h3>
                <div className="report-map-inner-container">
                  <Map
                    id="fieldReportMap"
                    ref={mapRef}
                    mapboxAccessToken="pk.eyJ1IjoiY2h1Y2swNTIwIiwiYSI6ImNrMDk2NDFhNTA0bW0zbHVuZTk3dHQ1cGUifQ.dkjP73KdE6JMTiLcUoHvUA"
                    initialViewState={{
                      longitude: -91,
                      latitude: 36,
                      preserveDrawingBuffer: true,
                      zoom: 4,
                    }}
                    mapStyle={baseLayer}
                    interactiveLayerIds={interactiveLayerIds}
                    onMouseMove={onMouseMove}
                    onLoad={() => setMapLoaded(true)}
                  >
                    <Tooltip placement="bottom" title="Layer Tool">
                      <Button
                        className="report-layer-select"
                        onClick={handleLayerSelect}
                      >
                        <LuMap strokeWidth={3} />
                      </Button>
                    </Tooltip>
                    {layerSelectOpen && (
                      <div className="report-layer-options">
                        <div className="base-layers">
                          <p>
                            <b>Base Layers</b>
                          </p>
                          <p>
                            <img
                              alt="thumbnail for light view of map"
                              className="layer-thumbnail"
                              src="https://api.mapbox.com/styles/v1/mapbox/light-v11/static/-88.81,33.45,7.0,0,0/50x50?access_token=pk.eyJ1IjoiY2h1Y2swNTIwIiwiYSI6ImNrMDk2NDFhNTA0bW0zbHVuZTk3dHQ1cGUifQ.dkjP73KdE6JMTiLcUoHvUA"
                            />
                            <Switch
                              className="report-layer-switch"
                              checked={
                                baseLayer ===
                                "mapbox://styles/mapbox/satellite-streets-v12"
                              }
                              checkedChildren="Satellite"
                              unCheckedChildren="light"
                              defaultChecked
                              onChange={(checked) => {
                                if (checked) {
                                  setBaseLayer(
                                    "mapbox://styles/mapbox/satellite-streets-v12"
                                  );
                                } else {
                                  setBaseLayer(
                                    "mapbox://styles/mapbox/light-v11"
                                  );
                                }
                              }}
                            />
                            <img
                              alt="thumbnail for light view of map"
                              className="layer-thumbnail"
                              src="https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/static/-88.81,33.45,7.0,0,0/50x50?access_token=pk.eyJ1IjoiY2h1Y2swNTIwIiwiYSI6ImNrMDk2NDFhNTA0bW0zbHVuZTk3dHQ1cGUifQ.dkjP73KdE6JMTiLcUoHvUA"
                            />
                          </p>
                        </div>
                        <hr />
                        <div className="supporting-layers">
                          <p>
                            <b>Supporting Layers</b>
                          </p>
                          <p>
                            <input
                              type="checkbox"
                              checked={soilLayerOn}
                              onClick={(e) => {
                                if (e.currentTarget.checked) {
                                  setSoilLayerOn(true);
                                } else {
                                  setSoilLayerOn(false);
                                }
                              }}
                            />
                            Soil Types
                            <div className="report-legend-soil"></div>
                          </p>
                          <p>
                            <input
                              type="checkbox"
                              checked={safeLayerOn}
                              onClick={(e) => {
                                if (e.currentTarget.checked) {
                                  setSafeLayerOn(true);
                                } else {
                                  setSafeLayerOn(false);
                                }
                              }}
                            />
                            SAFE Boundaries
                            <div className="report-legend-safe"></div>
                          </p>
                          <p>
                            <input
                              type="checkbox"
                              checked={floodplainLayerOn}
                              onClick={(e) => {
                                if (e.currentTarget.checked) {
                                  setFloodplainLayerOn(true);
                                } else {
                                  setFloodplainLayerOn(false);
                                }
                              }}
                            />
                            Floodplains
                            <div className="report-legend-floodplain"></div>
                          </p>
                          <p>
                            <input
                              type="checkbox"
                              checked={waterLayerOn}
                              onClick={(e) => {
                                if (e.currentTarget.checked) {
                                  setWaterLayerOn(true);
                                } else {
                                  setWaterLayerOn(false);
                                }
                              }}
                            />
                            Water Features
                            <div className="report-legend-water"></div>
                          </p>
                        </div>
                      </div>
                    )}
                    <Source
                      id="map-source"
                      type="vector"
                      url="mapbox://chuck0520.5pq9omti"
                      maxzoom={22}
                      minzoom={0}
                    >
                      <Layer
                        id="county-boundary"
                        source-layer="c_08mr23-1pp4eg"
                        type="line"
                        paint={{
                          "line-color": "#800000",
                          "line-width": 1,
                          "line-opacity": 0.4,
                        }}
                        minzoom={0}
                        maxzoom={22}
                        filter={stateFilter}
                      />
                    </Source>
                    <Source
                      id="field-source"
                      type="geojson"
                      data={fieldFeatureData}
                    >
                      <Layer
                        id="field"
                        type="line"
                        paint={{
                          "line-color": "white",
                          "line-width": 2,
                        }}
                      />
                    </Source>
                    {soilLayerOn && (
                      <Source id={"soil-source"} type="geojson" data={soilData}>
                        <Layer
                          id={"soil-layer"}
                          type="fill"
                          paint={{
                            "fill-outline-color": "white",
                            "fill-color": "transparent",
                          }}
                        />
                        <Layer
                          id={"soil-layer-highlight"}
                          type="fill"
                          paint={{
                            "fill-color": "purple",
                            "fill-opacity": 0.5,
                          }}
                          filter={filter}
                        />
                      </Source>
                    )}
                    {safeLayerOn && safeData && (
                      <Source id="safe-source" type="geojson" data={safeData}>
                        <Layer
                          id="safe"
                          type="fill"
                          paint={{
                            "fill-color": "orange",
                            "fill-opacity": 0.5,
                          }}
                        />
                      </Source>
                    )}
                    {floodplainLayerOn && floodplainData && (
                      <Source
                        id="floodplain-source"
                        type="geojson"
                        data={floodplainData}
                      >
                        <Layer
                          id="floodplain"
                          type="fill"
                          paint={{
                            "fill-color": "blue",
                            "fill-opacity": 0.5,
                          }}
                        />
                      </Source>
                    )}
                    {waterLayerOn && waterData && (
                      <Source id="water-source" type="geojson" data={waterData}>
                        <Layer
                          id="water"
                          type="fill"
                          paint={{
                            "fill-color": "cyan",
                            "fill-opacity": 0.5,
                          }}
                        />
                      </Source>
                    )}
                    {fieldDetails.fieldRate.rows.map((soil) => {
                      const soilGeometry = JSON.parse(soil.geometry);
                      const [lng, lat] = getMarkerLocation(soilGeometry);
                      return (
                        <>
                          {soilLayerOn && (
                            <Marker
                              longitude={lng}
                              latitude={lat}
                              anchor="center"
                            >
                              <label style={{ color: "white" }}>
                                {soil.musym}
                              </label>
                            </Marker>
                          )}
                        </>
                      );
                    })}
                  </Map>
                </div>
              </div>
              <div className="report-rental-table-container">
                <h3
                  className={
                    printList
                      ? "report-section-header print"
                      : "report-section-header"
                  }
                >
                  Base Rental Rate and Breakdown Table<sup>[1]</sup>:
                </h3>

                <p className="report-table-information">
                  This is a breakdown table of the soil types and corresponding
                  rental rate regarding your field. Please find more about soil
                  types in your county from{" "}
                  <a
                    href="https://www.nrcs.usda.gov/publications/Map%20Unit%20Description%20%28Brief%2C%20Generated%29%20Query%20by%20Soil%20Survey%20Area%20and%20Map%20Unit%20%28All%20Components%29.html"
                    target="_blank"
                    style={{ color: "maroon" }}
                    rel="noreferrer"
                  >
                    here
                  </a>
                  .
                </p>
                <Table
                  className={printList ? "print report-table" : "report-table"}
                  columns={columns}
                  dataSource={soilTableData}
                  pagination={{ pageSize: 3, position: ["bottomCenter"] }}
                  rowClassName={(record) =>
                    record.key === highlightFeatureId
                      ? "report-table-highlight"
                      : " "
                  }
                  size="large"
                />
              </div>
              <div>
                <h3
                  className={
                    printList
                      ? "report-section-header print"
                      : "report-section-header"
                  }
                >
                  Local USDA Office:
                </h3>
                <div
                  className={
                    printList
                      ? "report-usda-information print"
                      : "report-usda-information"
                  }
                >
                  <p>
                    Please reach out to your local USDA Farm Service Agency
                    office for further information.
                  </p>
                  <Table
                    className={
                      printList ? "print report-table" : "report-table"
                    }
                    columns={[
                      {
                        title: "Service Center",
                        dataIndex: "service_center",
                        key: "service_center",
                        align: "center",
                      },
                      {
                        title: "Address",
                        dataIndex: "physical_address",
                        key: "physical_address",
                        align: "center",
                      },
                      {
                        title: "Phone",
                        dataIndex: "phone",
                        key: "phone",
                        align: "center",
                      },
                    ]}
                    dataSource={serviceCenterList}
                    pagination={false}
                    size="large"
                  />
                </div>
              </div>
            </div>
          </div>
          <hr className="report-hr" />

          <div className="report-general-info-container">
            <div>
              <h3
                className={
                  printList
                    ? "report-section-header print"
                    : "report-section-header"
                }
              >
                Disclaimer:
              </h3>
              <div
                className={
                  printList ? "report-disclaimer print" : "report-disclaimer"
                }
              >
                <p>
                  [1] Please note that the estimated rental rate is a rough
                  estimation based on the CRP practice you selected. It may vary
                  due to your regional USDA official's final decision. All
                  dollar amounts presented are approximations derived from soil
                  type and rental rate data available at the time of this tool's
                  publication.
                </p>
                <p>
                  [2] This material is based upon work supported by the U.S.
                  Department of Agriculture, Farm Production and Conservation
                  program under agreement number FBC22CPT0012776. Any opinions,
                  findings, conclusions, or recommendations expressed in this
                  publication are those of the author(s) and do not necessarily
                  reflect the views of the U.S. Department of Agriculture. In
                  addition, any reference to specific brands or types of
                  products or services does not constitute or imply an
                  endorsement by the U.S. Department of Agriculture for those
                  products or services. USDA is an equal opportunity provider,
                  employer, and lender
                  (https://www.usda.gov/non-discrimination-statement). This work
                  is produced with support from the Mississippi State University
                  Forest and Wildlife Research Center and Mississippi State
                  University Geosystems Research Institute.
                </p>
              </div>
            </div>
          </div>
          <Modal
            centered={true}
            title="Save My Search"
            open={isSavePopOpen}
            onOk={null}
            footer={null}
            onCancel={saveSearchPopClose}
          >
            <Form
              name="basic"
              labelCol={{
                span: 8,
              }}
              form={form}
              wrapperCol={{
                span: 16,
              }}
              initialValues={{
                remember: true,
              }}
              onFinish={storeSearchData}
              // onFinishFailed={onFinishFailed}
              autoComplete="off"
            >
              <Form.Item
                label="Save Search"
                name="SaveKey"
                rules={[
                  {
                    required: true,
                    message: "Please input your Save Search!",
                  },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                wrapperCol={{
                  offset: 8,
                  span: 16,
                }}
              >
                <Button type="primary" htmlType="submit">
                  Submit
                </Button>
              </Form.Item>
            </Form>
          </Modal>
          <Modal
            centered={true}
            className="filter-modal"
            open={isWizardOpen}
            width="80vw"
            onOk={() => {
              dispatch(closeWizard());
            }}
            onCancel={() => {
              dispatch(closeWizard());
            }}
            cancelButtonProps={{ style: { display: "none" } }}
            okButtonProps={{ style: { display: "none" } }}
            footer={[
              wizardStep > 0 && (
                <Button
                  key="back"
                  style={{ margin: "0 8px" }}
                  onClick={() => dispatch(prevWizardStep())}
                >
                  Back
                </Button>
              ),
              wizardStep < 3 && (
                <Button
                  key="next"
                  className="location-sel-next"
                  style={{ float: "right" }}
                  type="primary"
                  onClick={() => dispatch(nextWizardStep())}
                >
                  Next
                </Button>
              ),
              wizardStep === 3 && (
                <Button
                  key="confirm"
                  className="confirm-tutorial"
                  style={{ float: "right" }}
                  type="primary"
                  onClick={() => dispatch(closeWizard())}
                >
                  Confirm
                </Button>
              ),
            ]}
          >
            <WizardForm
              className="filter-wizard-form"
              // selectedPriorities={onPrioritiesSelect}
              // selectedCheckboxes={selectedCheckboxes}
              // setRankingScore={onSetRankingScore}
              // notselected={notselected}
            />
          </Modal>
        </div>
      </Spin>
      <Footer />
    </>
  );
};

export default FieldReport;
